import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'

import {
  fetchPeople,
  fetchRegistration,
  fetchRegistrationHistory,
  fetchRegistrations,
  fetchRegistrationTickets,
  type RegistrationFilters,
} from '#admin/features/registrations/api.ts'

/**
 * The worklist, one page at a time.
 *
 * An infinite query because the server pages by cursor: "page 40" is not a question keyset
 * paging answers cheaply, and the list moves while an operator works it. The filters are part
 * of the key, so each filtered view is its own cache entry and Back is instant.
 */
export const registrationsQuery = (filters: RegistrationFilters) =>
  infiniteQueryOptions({
    queryKey: ['ticket', 'registrations', filters],
    queryFn: ({ pageParam, signal }) => fetchRegistrations(filters, pageParam, { signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  })

/**
 * How many registrations wait on verification, edition-wide — the list endpoint's `total` under
 * one filter, one row fetched. Under `['ticket']`, so an approve or reject refreshes it.
 */
export const pendingVerificationCountQuery = () =>
  queryOptions({
    queryKey: ['ticket', 'registrations', 'pending-verification'],
    queryFn: ({ signal }) =>
      fetchRegistrations(
        {
          q: '',
          status: '',
          paymentStatus: '',
          verificationStatus: 'pending',
          ticketTypeId: '',
          awaitingCompanyInvoice: '',
        },
        undefined,
        { signal, limit: 1 },
      ),
    select: (page) => page.total,
  })

/**
 * How many open registrations (not canceled or completed) are under the list's own filters. One
 * row fetched for each count, as above. "Select all" on the list offers this many. A filter on a
 * closed status matches no open registration, so the count is 0 with no request.
 */
export const openMatchingCountQuery = (filters: RegistrationFilters) =>
  queryOptions({
    queryKey: ['ticket', 'registrations', 'open-matching', filters],
    queryFn: async ({ signal }) => {
      const count = (status: RegistrationFilters['status']) =>
        fetchRegistrations({ ...filters, status }, undefined, { signal, limit: 1 }).then(
          (page) => page.total,
        )
      if (filters.status === 'canceled' || filters.status === 'completed') return 0
      // A submitted registration that is still open waits for its payment.
      if (filters.status === 'submitted') return count('pending_payment')
      if (filters.status !== '') return count(filters.status)
      // The list filters one status at a time, so "open" is all less the two closed statuses.
      const [all, canceled, completed] = await Promise.all([
        count(''),
        count('canceled'),
        count('completed'),
      ])
      return all - canceled - completed
    },
  })

/** How many registrations the edition has, for the dashboard. One row fetched, as above. */
export const registrationCountQuery = () =>
  queryOptions({
    queryKey: ['ticket', 'registrations', 'count'],
    queryFn: ({ signal }) =>
      fetchRegistrations(
        {
          q: '',
          status: '',
          paymentStatus: '',
          verificationStatus: '',
          ticketTypeId: '',
          awaitingCompanyInvoice: '',
        },
        undefined,
        { signal, limit: 1 },
      ),
    select: (page) => page.total,
  })

/** The five newest registrations, for the dashboard. The list endpoint sends the newest first. */
export const recentRegistrationsQuery = () =>
  queryOptions({
    queryKey: ['ticket', 'registrations', 'recent'],
    queryFn: ({ signal }) =>
      fetchRegistrations(
        {
          q: '',
          status: '',
          paymentStatus: '',
          verificationStatus: '',
          ticketTypeId: '',
          awaitingCompanyInvoice: '',
        },
        undefined,
        { signal, limit: 5 },
      ),
    select: (page) => page.items,
  })

export const registrationQuery = (id: string) =>
  queryOptions({
    queryKey: ['ticket', 'registration', id],
    queryFn: ({ signal }) => fetchRegistration(id, signal),
  })

// Under `['ticket']`, so confirm-payment and cancel refresh it with everything else.
export const registrationTicketsQuery = (id: string) =>
  queryOptions({
    queryKey: ['ticket', 'registration', id, 'tickets'],
    queryFn: ({ signal }) => fetchRegistrationTickets(id, signal),
  })

// Under `['ticket']` too: every mutation writes an audit row, and every mutation refreshes it.
export const registrationHistoryQuery = (id: string) =>
  queryOptions({
    queryKey: ['ticket', 'registration', id, 'history'],
    queryFn: ({ signal }) => fetchRegistrationHistory(id, signal),
  })

// Not under `['ticket']`: a registration mutation does not change who the edition's people are.
export const peopleQuery = (q: string) =>
  queryOptions({
    queryKey: ['people', q],
    queryFn: ({ signal }) => fetchPeople(q, signal),
    enabled: q.length >= 2,
  })
